import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function startCheckout() {
  const { data } = await axios.post(`${API}/payments/checkout`, {
    lookup_key: "campus2corp_ebook",
    origin_url: window.location.origin,
  });
  return data;
}

export async function getPaymentStatus(sessionId) {
  const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
  return data;
}

export async function submitBooking(payload) {
  const { data } = await axios.post(`${API}/bookings`, payload);
  return data;
}

export async function verifyBooking(payload) {
  const { data } = await axios.post(`${API}/bookings/verify`, payload);
  return data;
}

export async function streamChat(sessionId, message, { onDelta, signal }) {
  const res = await fetch(`${API}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, message }),
    signal,
  });
  if (!res.ok || !res.body) throw new Error("chat unavailable");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() || "";
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      const data = JSON.parse(line.slice(5).trim());
      if (data.delta) onDelta(data.delta);
      if (data.error) throw new Error(data.error);
      if (data.done) return;
    }
  }
}

export async function submitSupportLead(payload) {
  const { data } = await axios.post(`${API}/chat/lead`, payload);
  return data;
}

export async function submitLead(payload) {
  const { data } = await axios.post(`${API}/leads`, { ...payload, origin_url: window.location.origin });
  return data;
}

const TOKEN_KEY = "tw_admin_token";
export const adminToken = {
  get: () => sessionStorage.getItem(TOKEN_KEY),
  set: (t) => sessionStorage.setItem(TOKEN_KEY, t),
  clear: () => sessionStorage.removeItem(TOKEN_KEY),
};
const auth = () => ({ headers: { Authorization: `Bearer ${adminToken.get()}` } });

export const adminApi = {
  login: async (password) => (await axios.post(`${API}/admin/login`, { password })).data,
  stats: async () => (await axios.get(`${API}/admin/stats`, auth())).data,
  bookings: async () => (await axios.get(`${API}/admin/bookings`, auth())).data,
  updateBooking: async (id, body) => (await axios.patch(`${API}/admin/bookings/${id}`, body, auth())).data,
  purchases: async () => (await axios.get(`${API}/admin/purchases`, auth())).data,
  exportPurchasesCsv: async () => (await axios.get(`${API}/admin/purchases/export.csv`, { ...auth(), responseType: "blob" })).data,
  supportLeads: async () => (await axios.get(`${API}/admin/support-leads`, auth())).data,
  updateSupportLead: async (id, handled) => (await axios.patch(`${API}/admin/support-leads/${id}`, { handled }, auth())).data,
  exportCsv: async () => (await axios.get(`${API}/admin/bookings/export.csv`, { ...auth(), responseType: "blob" })).data,
  getContent: async () => (await axios.get(`${API}/admin/content`, auth())).data,
  saveContent: async (content) => (await axios.put(`${API}/admin/content`, { content }, auth())).data,
  saveSettings: async (notifyEmail) => (await axios.put(`${API}/admin/settings`, { notifyEmail }, auth())).data,
  uploadImage: async (slot, file) => {
    const fd = new FormData(); fd.append("file", file);
    return (await axios.post(`${API}/admin/upload/image?slot=${slot}`, fd, auth())).data;
  },
  uploadEbook: async (file) => {
    const fd = new FormData(); fd.append("file", file);
    return (await axios.post(`${API}/admin/upload/ebook`, fd, auth())).data;
  },
  deleteEbook: async () => (await axios.delete(`${API}/admin/upload/ebook`, auth())).data,
  uploadReward: async (file) => {
    const fd = new FormData(); fd.append("file", file);
    return (await axios.post(`${API}/admin/upload/reward`, fd, auth())).data;
  },
  deleteReward: async () => (await axios.delete(`${API}/admin/upload/reward`, auth())).data,
  getStripe: async () => (await axios.get(`${API}/admin/stripe`, auth())).data,
  saveStripe: async (payload) => (await axios.post(`${API}/admin/stripe`, payload, auth())).data,
  clearStripe: async () => (await axios.delete(`${API}/admin/stripe`, auth())).data,
  testStripe: async () => (await axios.post(`${API}/admin/stripe/test`, {}, auth())).data,
  unbookedBuyers: async () => (await axios.get(`${API}/admin/unbooked-buyers`, auth())).data,
  nudgeBooking: async (email) => (await axios.post(`${API}/admin/nudge-booking`, { email }, auth())).data,
  readers: async () => (await axios.get(`${API}/admin/readers`, auth())).data,
  nudgeReader: async (email) => (await axios.post(`${API}/admin/nudge-reader`, { email }, auth())).data,
  resetReaderPassword: async (email) => (await axios.post(`${API}/admin/reset-reader-password`, { email }, auth())).data,
  deleteImage: async (slot) => (await axios.delete(`${API}/admin/upload/image?slot=${slot}`, auth())).data,
  launch: async () => (await axios.get(`${API}/admin/launch`, auth())).data,
  setSiteMode: async (mode) => (await axios.put(`${API}/admin/site-mode`, { mode }, auth())).data,
  rotatePreview: async () => (await axios.post(`${API}/admin/preview-token/rotate`, {}, auth())).data,
  leads: async () => (await axios.get(`${API}/admin/leads`, auth())).data,
  deleteLead: async (id) => (await axios.delete(`${API}/admin/leads/${id}`, auth())).data,
  exportLeadsCsv: async () => (await axios.get(`${API}/admin/leads/export.csv`, { ...auth(), responseType: "blob" })).data,
  announceInfo: async () => (await axios.get(`${API}/admin/announce`, auth())).data,
  announceTest: async (body) => (await axios.post(`${API}/admin/announce/test`, { ...body, origin_url: window.location.origin }, auth())).data,
  announceSend: async (body) => (await axios.post(`${API}/admin/announce`, { ...body, origin_url: window.location.origin }, auth())).data,
  reviews: async () => (await axios.get(`${API}/admin/reviews`, auth())).data,
  updateReview: async (id, status) => (await axios.patch(`${API}/admin/reviews/${id}`, { status }, auth())).data,
  publishReview: async (id) => (await axios.post(`${API}/admin/reviews/${id}/publish`, {}, auth())).data,
  unpublishReview: async (id) => (await axios.delete(`${API}/admin/reviews/${id}/publish`, auth())).data,
  runReviewRequests: async () => (await axios.post(`${API}/admin/reviews/run`, {}, auth())).data,
  requestReview: async (sessionId) => (await axios.post(`${API}/admin/reviews/request/${sessionId}`, {}, auth())).data,
  resetDataInfo: async () => (await axios.get(`${API}/admin/reset-data/info`, auth())).data,
  resetData: async (collections, confirm) => (await axios.post(`${API}/admin/reset-data`, { collections, confirm }, auth())).data,
  resetDataUndo: async () => (await axios.post(`${API}/admin/reset-data/undo`, {}, auth())).data,
  createTestReader: async (email, password, name) => (await axios.post(`${API}/admin/create-test-reader`, { email, password, name }, auth())).data,
  restoreMedia: async (force = false) => (await axios.post(`${API}/admin/restore-media`, { force }, auth())).data,
};

export async function getReviewStatus(token) {
  const { data } = await axios.get(`${API}/reviews/${token}`);
  return data;
}

export async function submitReview(token, payload) {
  const { data } = await axios.post(`${API}/reviews/${token}`, payload);
  return data;
}

export const rewardDownloadUrl = (token) => `${API}/reviews/${token}/reward`;
